import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { changePassword } from '../api/client';
import type { Stage } from '../api/schemas';
import { Button, Field, INPUT_CLASS } from '../ui/elements';
import { ANMELDE_INPUT_CLASS, AnmeldeFeld } from './felder';
import { FieldError } from './LoginPage';

/**
 * Passwortwechsel — erzwungen aus der Kette oder freiwillig aus einer Sitzung.
 *
 * **Das aktuelle Passwort steht im Formular, auch im erzwungenen Fall.** Der
 * Server verlangt es, und das mit Grund (siehe `auth.service.ts`): Ohne diesen
 * Nachweis genügte ein gestohlenes Vor-Session-Cookie, um das Passwort zu
 * übernehmen. Es ist mit dem eben eingegebenen vorbelegt — der Anwender hat es
 * vor Sekunden getippt, und ein zweites Mal tippen zu lassen erzeugt nichts
 * ausser Tippfehlern.
 *
 * **Die Wiederholung wird geprüft, die Mindestlänge auch.** Beides ist
 * Bequemlichkeit und keine Zusage: Massgeblich ist die Prüfung im Dienst. Wer
 * hier vorbeikäme, scheiterte dort.
 *
 * ---------------------------------------------------------------------------
 * ZWEI BÜHNEN, EIN FORMULAR (Chip `W-loginredesign`)
 *
 * Dieses Formular steht an zwei Orten mit gegensätzlichen Massen: in der
 * Anmeldekette (`LoginPage`, Felder 44 px auf `--secondary` mit Sinnbild) und
 * in einer Tafel der angemeldeten Anwendung (`PasswordPage`, Felder 34 px, die
 * Vorgabe von `ui/shadcn/input.tsx`). Zwei Fassungen desselben Formulars wären
 * zwei Stellen, an denen die Mindestlänge steht; ein einziges Mass an beiden
 * Orten wäre an einem von beiden falsch — 44 px hohe Felder in einer Tafel
 * neben 34 px hohen Feldern jeder anderen Seite fielen sofort auf.
 *
 * Deshalb der Schalter `buehne`, und deshalb nur dieser eine: Er entscheidet
 * NUR über Feldform und Knopfmass, über nichts Fachliches. Die Vorgabe ist
 * `tafel` — wer ihn vergisst, bekommt das Aussehen der übrigen Anwendung.
 */

/** Zwölf Zeichen — dieselbe Zahl wie in `auth.service.ts`. */
const MIN_LENGTH = 12;

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Bitte das aktuelle Passwort eingeben.'),
    newPassword: z
      .string()
      .min(MIN_LENGTH, `Das neue Passwort muss mindestens ${String(MIN_LENGTH)} Zeichen haben.`),
    repeatPassword: z.string().min(1, 'Bitte das neue Passwort wiederholen.'),
  })
  .refine((values) => values.newPassword === values.repeatPassword, {
    path: ['repeatPassword'],
    message: 'Die beiden Eingaben stimmen nicht überein.',
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    path: ['newPassword'],
    message: 'Das neue Passwort muss sich vom bisherigen unterscheiden.',
  });

/** Die Eingaben des Formulars. */
type Values = z.infer<typeof schema>;

/** Wo das Formular steht — siehe den Kommentarblock oben. */
export type Buehne = 'anmeldung' | 'tafel';

export function PasswordChangeForm(props: {
  readonly currentPassword: string;
  readonly onStage: (stage: Stage, password: string) => void;
  readonly onFailure: (error: unknown) => void;
  readonly buehne?: Buehne;
}): ReactNode {
  const buehne = props.buehne ?? 'tafel';
  const anmeldung = buehne === 'anmeldung';
  const inputClass = anmeldung ? ANMELDE_INPUT_CLASS : INPUT_CLASS;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: props.currentPassword,
      newPassword: '',
      repeatPassword: '',
    },
  });

  const submit = async (values: Values): Promise<void> => {
    try {
      const stage = await changePassword(values.currentPassword, values.newPassword);
      props.onStage(stage, values.newPassword);
    } catch (error) {
      props.onFailure(error);
    }
  };

  return (
    <form
      className={anmeldung ? 'flex flex-col gap-4' : 'space-y-4'}
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit(submit)(event);
      }}
    >
      {/*
        **Die drei Beschriftungen bleiben wörtlich.** `e2e/first-login.spec.ts`
        greift «Aktuelles Passwort», «Neues Passwort» (exakt) und «Neues
        Passwort wiederholen» — alle drei über den zugänglichen Namen.
      */}
      <div>
        <PasswortFeld buehne={buehne} label="Aktuelles Passwort" sinnbild={<Lock />}>
          <input
            type="password"
            autoComplete="current-password"
            className={inputClass}
            {...form.register('currentPassword')}
          />
        </PasswortFeld>
        <FieldError message={form.formState.errors.currentPassword?.message} />
      </div>

      <div>
        <PasswortFeld
          buehne={buehne}
          label="Neues Passwort"
          sinnbild={<KeyRound />}
          hinweis={`Mindestens ${String(MIN_LENGTH)} Zeichen.`}
        >
          <input
            type="password"
            autoComplete="new-password"
            autoFocus
            className={inputClass}
            {...form.register('newPassword')}
          />
        </PasswortFeld>
        <FieldError message={form.formState.errors.newPassword?.message} />
      </div>

      <div>
        <PasswortFeld buehne={buehne} label="Neues Passwort wiederholen" sinnbild={<KeyRound />}>
          <input
            type="password"
            autoComplete="new-password"
            className={inputClass}
            {...form.register('repeatPassword')}
          />
        </PasswortFeld>
        <FieldError message={form.formState.errors.repeatPassword?.message} />
      </div>

      <Button
        type="submit"
        disabled={form.formState.isSubmitting}
        {...(anmeldung
          ? { className: 'mt-1.5 h-11 w-full rounded-[10px] text-sm font-semibold' }
          : {})}
      >
        Passwort ändern
      </Button>
    </form>
  );
}

/**
 * Das Feld in der Form der jeweiligen Bühne.
 *
 * **Der Hinweis bleibt in beiden Fassungen AUSSERHALB der Beschriftung.**
 * Läge er darin, hiesse das Feld «Neues Passwort Mindestens zwölf Zeichen»,
 * und `e2e/first-login.spec.ts` fände es nicht mehr — dieselbe Falle, die in
 * `ui/elements.tsx` bei `Field` beschrieben ist.
 */
function PasswortFeld(props: {
  readonly buehne: Buehne;
  readonly label: string;
  readonly sinnbild: ReactNode;
  readonly hinweis?: string;
  readonly children: ReactNode;
}): ReactNode {
  if (props.buehne === 'anmeldung') {
    return (
      <AnmeldeFeld
        label={props.label}
        sinnbild={props.sinnbild}
        {...(props.hinweis === undefined ? {} : { hinweis: props.hinweis })}
      >
        {props.children}
      </AnmeldeFeld>
    );
  }
  return (
    <Field label={props.label} {...(props.hinweis === undefined ? {} : { hint: props.hinweis })}>
      {props.children}
    </Field>
  );
}
